/**
 * 管理端剩余课时查询 API（ADM-34，entitlement-balance-inquiry）。
 *
 * 全部只读：学生×SKU 课时余额列表（姓名/SKU 模糊筛选、姓名+SKU 排序、
 * 购买剩余/赠送剩余/可用剩余三列，H-3 双池直读：赠送=赠送课池 gifted_available）与
 * 学生×SKU 课时流水（点击课时数字弹窗数据源，全账户全类型倒序）。
 * 不提供任何修改余额的入口。
 */
import { ApiPaths } from './api.config';
import type { Paginated } from './campuses.api';
import type { LessonKind, LedgerEntryType } from './entitlementLedger.api';
import { http } from './transport';

/** 学生×SKU 课时余额列表行。 */
export interface EntitlementBalanceItem {
  studentId: string;
  studentName: string;
  studentNo: string;
  skuId: string;
  skuName: string;
  /** 购买剩余（正式课池 formal_available）。 */
  purchasedRemaining: number;
  /** 赠送剩余（赠送课池 gifted_available）。 */
  giftedRemaining: number;
  /** 可用剩余 = 购买剩余 + 赠送剩余。 */
  availableTotal: number;
  periodPassSummary: string | null;
}

/** 学生×SKU 课时流水行（对齐 ADM-24 流水列表，附 SKU ID 与冲正原因；lessonKind=变动所属池）。 */
export interface EntitlementBalanceHistoryItem {
  id: string;
  createdAt: string;
  studentId: string;
  studentName: string;
  studentNo: string;
  accountId: string;
  skuId: string;
  skuName: string;
  lessonKind: LessonKind;
  type: LedgerEntryType;
  availableChange: number;
  availableAfter: number;
  businessRefType: string;
  businessRefId: string;
  operatorName: string | null;
  /** 冲正业务原因（仅 REVERSAL 流水非空）。 */
  reason: string | null;
}

/** 余额列表查询参数。 */
export interface EntitlementBalanceListQuery {
  studentName?: string;
  skuName?: string;
  page?: number;
  pageSize?: number;
}

/** 课时流水查询参数。 */
export interface EntitlementBalanceHistoryQuery {
  studentId: string;
  skuId: string;
  page?: number;
  pageSize?: number;
}

function buildQuery(base: string, query: object): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && `${value}` !== '') {
      params.set(key, `${value}`);
    }
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

async function listBalances(
  query: EntitlementBalanceListQuery = {},
): Promise<Paginated<EntitlementBalanceItem>> {
  return http.get<Paginated<EntitlementBalanceItem>>(
    buildQuery(ApiPaths.entitlementBalances.list, query),
  );
}

async function listHistory(
  query: EntitlementBalanceHistoryQuery,
): Promise<Paginated<EntitlementBalanceHistoryItem>> {
  return http.get<Paginated<EntitlementBalanceHistoryItem>>(
    buildQuery(ApiPaths.entitlementBalances.history, query),
  );
}

/** 剩余课时查询 API 唯一对外入口。 */
export const entitlementBalancesApi = {
  listBalances,
  listHistory,
};
